import {Box, Flex, List, ListItem, Text} from '@chakra-ui/react';
import {colors} from 'components/providers';
import {Checkbox, Counter} from 'components/shared';
import {FC} from 'react';
import {TranslationKeys} from 'translations';
import {IOptionSet, IOptionSets, OptionSetTypeEnum} from 'types';
import NextImageUI from 'ui/NextImage';

interface Props {
  sets?: IOptionSets[];
  onChange: (set: IOptionSets, option_set: IOptionSet) => void;
  selectedOptions: Record<number, Record<number, any>>;
  t: (term: TranslationKeys) => string;
}

export const OptionSets: FC<Props> = ({t, sets, onChange, selectedOptions}) => {
  if (!sets?.length) return null;

  return (
    <List>
      {sets.map((set) => {
        const {id, option_set, max_values, name, type} = set;
        let quantity = 0;
        if (selectedOptions[set.id]) {
          Object.values(selectedOptions[set.id]).forEach((option) => {
            if (option.quantity) {
              quantity += option.quantity;
            }
          });
        }

        const isRequired = !!max_values &&
          set.min_values > 0 &&
          quantity < max_values && {
            borderColor: colors.red,
            borderWidth: '1px',
            borderRadius: '5px',
            padding: ['2px', '5px'],
          };

        return (
          <ListItem
            id={`${id}`}
            key={id}
            mb={['15px', '36px']}
            sx={
              isRequired || {
                borderColor: 'transparent',
                borderWidth: '1px',
                borderRadius: '5px',
                padding: ['2px', '5px'],
              }
            }
          >
            <Box mb={['15px', '30px']}>
              <Text fontSize={['16px', '18px']} fontWeight={600} mb="15px">
                {name}
              </Text>
              {!!max_values && (
                <>
                  {set.min_values > 0 ? (
                    <Flex align="center">
                      <Text fontSize={['14px', '16px']} color={colors.textGray} fontWeight={500}>
                        {t('SELECT')} {max_values} {t('OPTIONS')}
                      </Text>
                      <Box ml={2} background="palette.pink" px={2} borderRadius="999px" color="white">
                        {t('IS_REQUIRED')}
                      </Box>
                      <Text
                        fontWeight="bold"
                        ml="5px"
                        fontSize={['10px', '14px']}
                      >{` (${quantity} / ${max_values}) `}</Text>
                    </Flex>
                  ) : (
                    <Text fontSize="16px" color={colors.textGray} fontWeight={500}>
                      {t('SELECT_MAX_OPTIONS')} {max_values} {t('OPTIONS')}
                    </Text>
                  )}
                </>
              )}
            </Box>
            <List>
              {option_set?.map((option_set) => {
                const {id, name, price, max_qty, without} = option_set;
                const isCheckOption = selectedOptions[set.id]?.hasOwnProperty(id);
                const isMaxQuantity = max_qty > 1;

                const quantity = isCheckOption ? selectedOptions[set.id][id].quantity : 1;
                const checkboxType = type === OptionSetTypeEnum.ONE ? 'circle' : 'square';

                let temp = 0;

                for (const key in selectedOptions[set.id]) {
                  if (selectedOptions[set.id][key].quantity) {
                    temp = selectedOptions[set.id][key].quantity + temp;
                  }
                }

                const isFullySelected = max_values / temp === 1;
                const isDisabled = !isCheckOption && isFullySelected && type !== OptionSetTypeEnum.ONE;

                const onChangeOp = (value: number) => {
                  onChange(set, {...option_set, quantity: value});
                };

                return (
                  <Flex
                    key={id}
                    justifyContent="space-between"
                    alignItems="center"
                    borderTop="1px"
                    borderColor={colors.borderGray}
                  >
                    <ListItem display="flex" justifyContent="space-between" h="56px" alignItems="center">
                      {option_set?.img_thumbnail && (
                        <Box pr="16px">
                          <NextImageUI width={70} height={50} src={option_set?.img_thumbnail} alt={`Option ${name}`} />
                        </Box>
                      )}

                      <Checkbox
                        cursor={isDisabled ? 'no-drop' : 'pointer'}
                        variant={checkboxType}
                        isDisabled={isDisabled}
                        onChange={() => {
                          // because of max_qty max_values macdonalds | chi_wok
                          const maxAvailableQty = isCheckOption
                            ? 0
                            : max_values - temp > 0
                            ? set.min_values > 0
                              ? max_values - temp
                              : 1
                            : max_qty
                            ? max_qty
                            : 1;

                          onChange(set, {
                            ...option_set,
                            quantity: maxAvailableQty,
                          });
                        }}
                        isChecked={isCheckOption}
                        _focus={{border: 'none'}}
                      >
                        <Text as="span" fontSize={['14px', '18px']} fontWeight={500}>
                          {name} +
                          <Text as="span" fontWeight={500} ml="5px">
                            {price} {t('CURRENCY')}
                          </Text>
                        </Text>
                      </Checkbox>
                    </ListItem>
                    {isCheckOption && isMaxQuantity && (
                      <Box ml="15px">
                        <Counter
                          isSmallSize
                          isDisabled={max_values === temp}
                          defaultValue={without ? 1 : quantity}
                          onChange={onChangeOp}
                          max={without ? 1 : max_qty}
                        />
                      </Box>
                    )}
                  </Flex>
                );
              })}
            </List>
          </ListItem>
        );
      })}
    </List>
  );
};
